// store/modules/ArticleVuex.ts
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import ItemService from '@/Services/ItemsService';
import Item from '@/models/Items';
import { isErrorResponse } from '@/utils/typeGuards';

@Module({ namespaced: true })
export default class ItemModule extends VuexModule {
    Items: Item[] = [];
    error: string | null = null;
    Item: Item | null = null;

    @Mutation
    setArticles(Items: Item[]) {
        this.Items = Items;
    }

    @Mutation
    setArticle(Item: Item) {
        this.Item = Item;
    }

    @Mutation
    setError(error: string | null) {
        this.error = error;
    }

    @Action
    async fetchItems(url: string) {
        try {

            const response = await ItemService.getAll(url);

            // Si c'est un export (blob), retourner directement la réponse AxiosResponse
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            if (isExport) {
                return response; // AxiosResponse avec blob
            }

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching items.');
                return null;
            }

            // Stocker les données dans Vuex (seulement pour les requêtes normales)
            if (response.data) {
                this.context.commit('setArticles', response.data);
            }

            // Retourner la réponse complète pour supporter DataTables
            return response;
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while fetching items.');
            return null;
        }
    }

    @Action
    async extractItems(queryParams: string) {
        try {
            const response = await ItemService.extractAll(queryParams);
            return response; // Retourner directement la réponse blob
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while extracting items.');
            throw error;
        }
    }

    @Action
    async fetchArticlesArchive(url: string) {
        try {
            const response = await ItemService.getItemArchive(url);

            // Si c'est un export (blob), retourner directement la réponse AxiosResponse
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            if (isExport) {
                return response; // AxiosResponse avec blob
            }

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while extracting articles.');
                return null;
            }

            // Mettre à jour le state avec les données (seulement pour les requêtes normales)
            if (response.data) {
                this.context.commit('setArticles', response.data);
            }

            // Retourner le format DataTable complet
            return response;
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while fetching articles archive.');
            return null;
        }
    }
    @Action
    async archiveItem({ id, commentaire, action }: { id: number, commentaire?: string, action: string }) {
        try {
            const response = await ItemService.archiveItem({ id, commentaire: commentaire || '', action });

            // Vérifiez si une réponse valide est retournée
            if (response && response.message) {
                // Met à jour l'état Vuex si nécessaire
                this.context.commit('setArticles', response);
                return response; // Retourne { message: "Item archived successfully" }
            } else {
                throw new Error('Réponse invalide du service.');
            }
        } catch (error: any) {
            // Enregistre et retourne une erreur cohérente
            const errorMessage = error.message || 'Une erreur est survenue lors de l\'archivage.';
            this.context.commit('setError', errorMessage);
            throw new Error(errorMessage);
        }
    }

    @Action
    async archiveItemsBatch(itemsId: number[]) {
        try {
            const response = await ItemService.archiveItemsBatch(itemsId);

            if (response) {
                return response;
            }

            throw new Error('Réponse invalide du service lors de l\'archivage en masse.');
        } catch (error: any) {
            const errorMessage = error.message || 'Une erreur est survenue lors de l\'archivage en masse.';
            this.context.commit('setError', errorMessage);
            throw new Error(errorMessage);
        }
    }

    @Action
    async importArchiveItemsExcel({ file, skipErrors }: { file: File; skipErrors: boolean }) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('skip_errors', String(skipErrors));

            const response = await ItemService.importArchiveItemsExcel(formData);

            if (response) {
                return response;
            }

            throw new Error('Réponse invalide du service lors de l\'import Excel d\'archivage.');
        } catch (error: any) {
            let errorMessage = 'Une erreur est survenue lors de l\'import Excel d\'archivage.';

            // Préférence au message fonctionnel renvoyé par le backend
            const backendData = error?.response?.data;
            if (backendData) {
                if (Array.isArray(backendData.errors) && backendData.errors.length > 0) {
                    errorMessage = backendData.errors.join('\n');
                } else if (backendData.message) {
                    errorMessage = backendData.message;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            this.context.commit('setError', errorMessage);
            throw new Error(errorMessage);
        }
    }

    @Action
    async AnnulerAffectation(id: number) {
        try {
            const response = await ItemService.AnnulerAffectation({ id });

            if (response && response.message) {
                return response;
            }
            return null;
        } catch (error: any) {
            console.error('Erreur annulation affectation:', error);
            return null;
        }
    }


    @Action
    async getItemById(id: number) {
        const response = await ItemService.getByIdItem(id);
        if (isErrorResponse(response)) {
            throw new Error(response.message);
        }
        return response;
    }

    @Action
    async updateItem({ Id, Items }: { Id: number, Items: any }) {
        const response = await ItemService.updateItem({ Id, Items });
        if (isErrorResponse(response)) {
            throw new Error(response.message);
        }
        return response;
    }
    get getAll() {
        return this.Items;
    }
    get getItem() {
        return this.Item;
    }

}
